import { useEffect, useState } from 'react'
import type { FeasibilityPipelineStatus, FeasibilityReport as FeasibilityReportT } from '../api'
import { cancelFeasibility, getFeasibilityReport, getFeasibilityStatus } from '../api'
import { BrandMark } from './BrandMark'
import { MorphingMark } from './MorphingMark'
import { Report } from './Report'

/**
 * Landing page for both fresh submissions (redirected here from
 * the address form) and email-link returns.
 *
 * Under Phase 2 the POST /api/feasibility submit is fire-and-forget:
 * it creates a placeholder row, schedules the whole pipeline as a
 * background task, and returns a report id. This page polls
 * /feasibility/{id}/status every 2 s while the pipeline runs and
 * loads the full report once status flips to 'ok'. Failure modes
 * (NSW Planning Portal timeout, validation errors) surface with
 * retry / dismiss affordances.
 *
 * Resilient by design — the planner can close the tab any time
 * after submitting and the work continues server-side. Coming back
 * via the email link or the URL just resumes from wherever the
 * pipeline is now.
 */
export function ReportByIdPage({ reportId }: { reportId: string }) {
  const [status, setStatus] = useState<FeasibilityPipelineStatus | null>(null)
  const [report, setReport] = useState<FeasibilityReportT | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [elapsedSec, setElapsedSec] = useState<number>(0)
  // Disable the Cancel button between click and the next status
  // poll so it can't fire twice or flicker when the click races
  // with the pipeline finishing.
  const [cancelling, setCancelling] = useState<boolean>(false)

  async function onCancel() {
    if (cancelling) return
    setCancelling(true)
    try {
      const s = await cancelFeasibility(reportId)
      setStatus(s)
    } catch {
      // Status will reconcile on the next poll; nothing actionable
      // to show here.
    } finally {
      setCancelling(false)
    }
  }

  // Poll status every 2 s until terminal (ok / failed). The first
  // poll fires immediately so the page doesn't sit blank for 2 s
  // when the pipeline is already done (e.g. opening an email link
  // after the analyst has long since finished).
  useEffect(() => {
    let cancelled = false
    let interval: number | null = null

    async function poll() {
      try {
        const s = await getFeasibilityStatus(reportId)
        if (cancelled) return
        setStatus(s)
        if (s.pipeline_status === 'ok') {
          // Pipeline complete — fetch the full report payload and
          // hand off to <Report />.
          if (interval !== null) {
            window.clearInterval(interval)
            interval = null
          }
          try {
            const r = await getFeasibilityReport(reportId)
            if (!cancelled) setReport(r)
          } catch (e) {
            if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e))
          }
        } else if (s.pipeline_status === 'failed' || s.pipeline_status === 'cancelled') {
          if (interval !== null) {
            window.clearInterval(interval)
            interval = null
          }
        }
      } catch (e) {
        // Status endpoint blip — swallow and let the next interval
        // retry. 404 (report not found) just keeps polling because
        // the placeholder row is committed BEFORE the response
        // returns, so this is essentially impossible in practice.
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e)
          if (msg.includes('404') || msg.includes('not found')) {
            // Persistent 404 → something's wrong. Bail.
            setLoadError('Report not found. The link may have expired or you may need to sign in.')
            if (interval !== null) {
              window.clearInterval(interval)
              interval = null
            }
          }
        }
      }
    }

    poll()
    interval = window.setInterval(poll, 2000)
    return () => {
      cancelled = true
      if (interval !== null) window.clearInterval(interval)
    }
  }, [reportId])

  // Wall-clock counter so the planner can see the pipeline is alive.
  useEffect(() => {
    if (
      status?.pipeline_status === 'ok'
      || status?.pipeline_status === 'failed'
      || status?.pipeline_status === 'cancelled'
    ) return
    const start = Date.now()
    const t = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - start) / 1000))
    }, 1000)
    return () => window.clearInterval(t)
  }, [status?.pipeline_status])

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-10 backdrop-blur-md header-bg border-b border-massing-rule">
        {/* Same responsive treatment as the main App header — desktop
            keeps the 120 px brand spec, mobile shrinks to 60 px and
            hides the subtitle so the lockup fits a 375 px viewport
            without horizontal overflow. */}
        <div className="max-w-6xl mx-auto px-4 sm:px-10 h-[72px] sm:h-[117px] py-4 sm:py-7 flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-3 sm:gap-8 no-underline min-w-0">
            <span className="flex items-center gap-2 sm:gap-[14px] shrink-0">
              <BrandMark className="w-[60px] sm:w-[120px]" />
              <span className="brand-wordmark brand-gradient-text leading-none text-[24px] sm:text-[38px]">massing</span>
            </span>
            <span className="brand-eyebrow text-massing-ink-light hidden sm:inline">NSW residential feasibility</span>
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-10 py-6 sm:py-10">
        {/* Failure state — pipeline finished with an error, or the
            status endpoint persistently 404s. Show retry/back. */}
        {(status?.pipeline_status === 'failed' || loadError) && (
          <div className={
            'rounded-md p-5 ' +
            (status?.error_kind === 'portal'
              ? 'border border-flag-amber/40 bg-flag-amber/10 text-massing-ink'
              : 'border border-massing-rule bg-massing-maroon-soft text-massing-maroon')
          }>
            {status?.error_kind === 'portal' && (
              <div className="brand-eyebrow text-flag-amber mb-1.5">
                NSW Planning Portal — temporarily slow
              </div>
            )}
            <div className="font-medium mb-1">Couldn't finish this feasibility run</div>
            <p className="text-sm leading-snug">
              {status?.error_detail || loadError}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <a
                href="/"
                className="bg-massing hover:bg-massing-deep text-massing-cream px-4 py-1.5 text-sm tracking-wide rounded-sm no-underline"
              >
                Back to address form
              </a>
            </div>
          </div>
        )}

        {/* Cancelled state — planner clicked Cancel; pipeline stops
            at next decision point, no email is sent. */}
        {status?.pipeline_status === 'cancelled' && (
          <div className="rounded-md border border-massing-rule bg-massing-card p-5 text-massing-ink">
            <div className="brand-eyebrow text-massing-ink-light mb-1.5">
              Run cancelled
            </div>
            <div className="font-medium mb-1">No email will be sent</div>
            <p className="text-sm leading-snug text-massing-ink-mid">
              The background pipeline is stopped at the next safe
              checkpoint. The work already done is wasted but no
              notification goes out. You can submit a fresh
              feasibility from the address form.
            </p>
            <div className="mt-3">
              <a
                href="/"
                className="bg-massing hover:bg-massing-deep text-massing-cream px-4 py-1.5 text-sm tracking-wide rounded-sm no-underline"
              >
                Back to address form
              </a>
            </div>
          </div>
        )}

        {/* In-progress state — polling, no terminal status yet. */}
        {!report
          && status?.pipeline_status !== 'failed'
          && status?.pipeline_status !== 'cancelled'
          && !loadError && (
          <>
            <PipelineRunningPanel
              elapsedSec={elapsedSec}
              address={status?.address ?? null}
              queued={status?.pipeline_status === 'queued'}
              onCancel={onCancel}
              cancelling={cancelling}
            />
            <WhileYouWaitCard elapsedSec={elapsedSec} />
          </>
        )}

        {/* Loaded — render the full report. */}
        {report && <Report report={report} />}
      </main>
    </div>
  )
}


/**
 * Live loading panel for /r/<id> while the background pipeline
 * runs. Mirrors the visual treatment of the FindingsAnalysisRunning
 * panel from Report.tsx — gradient orb with ping ring, headline in
 * gradient text, cycling current-task in gradient text below, fading
 * trail of recently completed tasks, and a brand-gradient progress
 * bar in the footer. The backend doesn't emit per-stage progress
 * events (each stage is opaque httpx / Anthropic / postgres work),
 * so this is essentially a locally-timed ticker — but the labels
 * are pinned to the actual pipeline order so the rotation roughly
 * tracks reality.
 */
const PIPELINE_TASKS: string[] = [
  'Geocoding the address',
  'Finding the cadastre lot',
  'Computing lot geometry + frontage',
  'Querying NSW Planning Portal spatial overlays',
  'Reading the LEP zone + FSR + height-of-buildings maps',
  'Reading bushfire / flood / heritage / biodiversity layers',
  'Cross-validating LEP findings against the council cache',
  'Reading the DCP cache for this council',
  'Computing the Housing Code CDC envelope',
  'Checking dual-occupancy feasibility (where relevant)',
  'Building the Council DA pathway',
  'Building the CDC verdict + next-steps',
  'Reconciling findings into the analyst layer',
  'Cross-checking bushfire-prone overlays + BAL implications',
  'Reviewing contaminated-land findings',
  'Reading classified-road frontage controls',
  'Reading easements, covenants, and 88B encumbrances',
  'Stitching the report together',
]

// ~3.5 s per task — gives the planner enough time to read each one
// without feeling sluggish. The full list (18 tasks) cycles in ~63 s,
// which matches the typical pipeline runtime; if the run takes longer,
// the panel holds on the final "Stitching…" task.
const PIPELINE_TASK_INTERVAL_S = 3.5

function PipelineRunningPanel({
  elapsedSec,
  address,
  queued,
  onCancel,
  cancelling,
}: {
  elapsedSec: number
  address: string | null
  queued: boolean
  onCancel: () => void
  cancelling: boolean
}) {
  const rawIdx = Math.floor(elapsedSec / PIPELINE_TASK_INTERVAL_S)
  const idx = Math.min(rawIdx, PIPELINE_TASKS.length - 1)
  const currentTask = PIPELINE_TASKS[idx]
  // Last 3 completed tasks form a fading trail under the active row.
  const recent = PIPELINE_TASKS.slice(Math.max(0, idx - 3), idx).reverse()
  // Progress bar fills proportional to position through the list.
  // Caps at ~94 % so it never reads "done" before the real result.
  const progressPct = Math.min(94, ((idx + 1) / PIPELINE_TASKS.length) * 100)

  return (
    <section className="rounded-xl border border-massing-rule glass overflow-hidden relative">
      {/* Animated background wash — two layered radial gradients
          on a slow opposing drift, plus a slow hue rotation. Reads
          as "the engine is alive" without competing with the
          morphing mark for attention. Pinned behind the content
          via absolute positioning + pointer-events:none so it
          doesn't intercept the Cancel-button click. */}
      <div
        className="absolute inset-0 pointer-events-none animate-pipeline-wash"
        aria-hidden
        style={{
          background:
            'radial-gradient(40% 50% at 20% 30%, rgba(255, 106, 213, 0.35), transparent 70%), '
            + 'radial-gradient(45% 55% at 80% 70%, rgba(118, 231, 255, 0.30), transparent 70%), '
            + 'radial-gradient(60% 60% at 50% 50%, rgba(173, 140, 255, 0.20), transparent 70%)',
          backgroundSize: '160% 160%, 160% 160%, 200% 200%',
        }}
      />

      {/* Progress bar at the TOP — was at the bottom + below the
          fold on first paint, which defeated the point of having a
          live progress indicator. Top placement also matches the
          conventional "browser-style" loading affordance. */}
      <div className="relative h-[3px] bg-massing-rule-soft overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 brand-gradient-bg transition-all duration-700 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Hero: morphing-shape animation (logo → house → pin → tree
          → chart) with goo filter — the centerpiece of the loading
          screen. Smaller than before (140 px vs 220 px) + tighter
          vertical padding so the whole panel fits above the mobile
          fold instead of pushing the live ticker off-screen. */}
      <div className="relative flex items-center justify-center py-6">
        <MorphingMark size={140} />
        <span className="absolute top-3 right-4 text-[11px] text-massing-ink-light font-mono">
          {elapsedSec}s
        </span>
      </div>

      <div className="relative px-6 py-3 border-t border-massing-rule-soft">
        <div className="brand-eyebrow text-massing-ink-light mb-1">
          {queued ? 'Queued' : 'Preparing your report'}
        </div>
        <div className="flex items-center gap-3">
          <span className="font-serif text-lg brand-gradient-text leading-snug">
            {queued ? 'Starting up your run…' : 'Running your feasibility…'}
          </span>
        </div>
        <p className="text-[12.5px] text-massing-ink-mid leading-snug mt-1.5">
          {address ? <>For <strong>{address}</strong>. </> : null}
          The pipeline runs server-side — you can close this tab and
          come back via the email link when it's ready.
        </p>
      </div>

      {/* Live ticker — current task in gradient text, recent tasks
          fading below. Key on idx so React swaps the node and the
          transition restarts each cycle. */}
      <div className="relative px-6 py-3 space-y-2 border-t border-massing-rule-soft">
        <div className="flex items-start gap-3" key={`cur-${idx}`}>
          <span
            className="inline-block w-2 h-2 rounded-full brand-gradient-bg mt-2 shrink-0 animate-pulse"
            aria-hidden
          />
          <span className="text-[14px] font-medium brand-gradient-text leading-snug">
            {currentTask}…
          </span>
        </div>
        <div className="space-y-1 pl-5">
          {recent.map((task, i) => (
            <div
              key={`r-${idx}-${i}`}
              className="text-[11.5px] text-massing-ink-light leading-snug flex items-center gap-2"
              style={{ opacity: 0.85 - i * 0.22 }}
            >
              <span className="text-massing-ink-light/70">✓</span>
              <span>{task}</span>
            </div>
          ))}
        </div>
        <div className="pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={cancelling}
            className="text-[11.5px] uppercase tracking-[0.12em] text-massing-ink-light hover:text-massing-ink disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelling ? 'Cancelling…' : 'Cancel run'}
          </button>
        </div>
      </div>
    </section>
  )
}


/**
 * "While you wait" card — rotates through curated NSW residential
 * planning hints, statistics, and recent regulatory news while the
 * background pipeline runs. Gives the planner something useful to
 * absorb during the 30-90s wait instead of staring at the morph.
 *
 * Content is hand-curated against current NSW planning sources
 * (LEPs, SEPPs, NSW Govt housing reforms). Categories surface as
 * a small chip so the planner can tell tip vs stat vs news vs
 * micro-lesson at a glance.
 */
type WaitCard = {
  category: 'TIP' | 'STAT' | 'NEWS' | 'LEARN'
  title: string
  body: string
}

const WAIT_CARDS: WaitCard[] = [
  // ---- Recent regulatory news (most actionable) ----
  {
    category: 'NEWS',
    title: 'LMR Housing SEPP (Feb 2024)',
    body:
      'Dual occupancies are now permitted in every R2 zone state-wide, '
      + 'with a 225 m² minimum subdivision floor — overrides council LEPs '
      + 'with stricter minimums. Worth checking if your lot was previously '
      + 'flagged ineligible.',
  },
  {
    category: 'NEWS',
    title: 'TOD SEPP (Aug 2024)',
    body:
      'Land within ~800 m of selected NSW train stations was rezoned to '
      + 'allow mid-rise housing. If your lot is in a TOD precinct, the '
      + 'height + FSR caps are different from the local LEP.',
  },
  {
    category: 'NEWS',
    title: 'Housing Code consolidation (2024)',
    body:
      'NSW Govt merged the General Housing + Rural Housing + LRHD codes '
      + 'into a single Housing Code under the Codes SEPP. Same CDC '
      + 'pathways, simpler navigation.',
  },

  // ---- Stats (concrete, evidence-anchored) ----
  {
    category: 'STAT',
    title: 'CDC vs DA timing',
    body:
      'Median CDC approval in NSW: ~17 business days. Median Council DA: '
      + '~78 business days. The biggest single factor on which pathway '
      + 'is right is your timeline.',
  },
  {
    category: 'STAT',
    title: 'How NSW builds',
    body:
      '~85,000 dwelling approvals issued in NSW in 2023; about 70% went '
      + 'through CDC under the Housing Code. The other 30% needed a '
      + 'council DA, usually because of an overlay or non-standard form.',
  },
  {
    category: 'STAT',
    title: 'Overlay prevalence',
    body:
      'Roughly 12% of NSW residential lots carry a heritage, biodiversity, '
      + 'flood, or bushfire overlay that materially affects feasibility. '
      + 'Your Section 10.7 is the source of truth on this.',
  },

  // ---- Tips (free wins) ----
  {
    category: 'TIP',
    title: 'Book a free Pre-DA meeting',
    body:
      'Most NSW councils offer a Pre-DA meeting where you can workshop '
      + 'your design with a council planner before lodging. Free or '
      + 'low-cost, saves weeks of formal RFI loops later.',
  },
  {
    category: 'TIP',
    title: 'Order the 10.7 early',
    body:
      'A Section 10.7(2) certificate ($133 in most councils) tells you '
      + 'exactly what overlays apply — bushfire, flood, heritage, '
      + 'contamination, biodiversity. Get it before locking in your '
      + 'design brief, not after.',
  },
  {
    category: 'TIP',
    title: 'Corner lots = extra rules',
    body:
      'On a corner lot, secondary-street setbacks apply alongside the '
      + 'front setback. If one of those streets is a Classified Road '
      + '(SP2), the CDC front setback jumps to 9 m on that face.',
  },
  {
    category: 'TIP',
    title: 'BAL assessment: $600 – $1,800',
    body:
      'A bushfire BAL assessment under AS 3959 is needed for any '
      + 'bushfire-prone lot. Takes 1–2 weeks. BAL-40 or Flame Zone '
      + 'disqualifies CDC and forces a Council DA with RFS concurrence.',
  },

  // ---- Quick-learn micro-lessons ----
  {
    category: 'LEARN',
    title: 'What FSR actually means',
    body:
      'Floor Space Ratio = total internal floor area ÷ lot area. A 0.5:1 '
      + 'FSR on a 600 m² lot lets you build 300 m² of gross floor area '
      + 'total (across all storeys).',
  },
  {
    category: 'LEARN',
    title: 'Torrens vs Strata subdivision',
    body:
      'Torrens splits the land into separate titled lots — each must '
      + 'meet the LEP minimum. Strata splits the building into separately '
      + 'titled units sharing the land — usually no minimum lot rule.',
  },
  {
    category: 'LEARN',
    title: 'Setback vs envelope',
    body:
      'Setback is the distance from your boundary to the building wall. '
      + 'The "envelope" is the 3D box your build has to fit inside — '
      + 'set by setbacks + max height + max storeys + landscaped area.',
  },
  {
    category: 'LEARN',
    title: 'Cl 4.6 variation',
    body:
      'Most NSW LEPs have a Clause 4.6 letting council vary a development '
      + 'standard if you can prove compliance is "unreasonable or '
      + 'unnecessary" for the site. Often the only path around a '
      + 'too-strict cl 4.3 height.',
  },
]

// One card per ~10 seconds. Slow enough to actually read; fast
// enough to cycle through ~6 cards in a typical 60 s pipeline run.
const WAIT_CARD_INTERVAL_S = 10

function WhileYouWaitCard({ elapsedSec }: { elapsedSec: number }) {
  const idx = Math.floor(elapsedSec / WAIT_CARD_INTERVAL_S) % WAIT_CARDS.length
  const card = WAIT_CARDS[idx]

  const chipColor =
    card.category === 'NEWS'
      ? 'bg-flag-amber/15 text-flag-amber border border-flag-amber/30'
      : card.category === 'STAT'
      ? 'bg-massing-highlight/15 text-massing-highlight border border-massing-highlight/30'
      : card.category === 'TIP'
      ? 'bg-flag-green/15 text-flag-green border border-flag-green/30'
      : 'bg-massing-rule-soft text-massing-ink-mid border border-massing-rule'

  return (
    <section className="mt-6 rounded-xl border border-massing-rule glass overflow-hidden">
      <div className="px-6 py-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="brand-eyebrow text-massing-ink-light">
            While you wait
          </div>
          <span className="text-[11px] text-massing-ink-light font-mono ml-auto">
            {idx + 1} / {WAIT_CARDS.length}
          </span>
        </div>
        <div key={idx} className="space-y-2 animate-fadein">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 rounded ${chipColor}`}>
              {card.category}
            </span>
            <span className="font-serif text-lg text-massing-ink leading-snug">
              {card.title}
            </span>
          </div>
          <p className="text-[13px] text-massing-ink-mid leading-relaxed">
            {card.body}
          </p>
        </div>
      </div>
    </section>
  )
}
